import React from 'react'

import { Languages } from '../../../configuration/constants/enums'
import { useI18n } from '../../../configuration/contexts/I18nContext'
import styled from '../../constants/theme'
import { useUpdatePatientProfile } from '../../hooks/useUpdatePatientProfile'
import PreferredLanguageDialog from '../dialogs/PreferredLanguageDialog'
import { GlobeIcon, SelectorIcon } from '../elements/Icons'
import { setPreferredLanguage } from '../../lib/storage'

interface Props {
  id: string
}

export const PreferredLanguageSelection: React.FC<Props> = ({ id }) => {
  const access_token = window.CMD.user?.access_token
  const { languageCode } = useI18n()
  const [languageCodeState, setLanguageCodeState] = React.useState(languageCode)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const { updatePatientProfile } = useUpdatePatientProfile()

  const closeModal = React.useCallback(() => {
    setIsModalOpen(false)
    setLanguageCodeState(languageCode)
  }, [languageCode])

  const handleOnConfirm = async () => {
    setIsModalOpen(false)
    await updatePatientProfile({
      'preferences.preferredLanguage': languageCodeState,
    })
    window.location.reload()
  }

  const handleOnChange = React.useCallback(
    (e) => {
      e.preventDefault()
      const { value } = e.target

      if (languageCodeState !== value) {
        setLanguageCodeState(value)
      }

      if (languageCode !== value) {
        setPreferredLanguage(value)
      }

      // We're logged in -- proceed as normal
      if (access_token) {
        setIsModalOpen(true)
        return
      }

      // We're not logged in -- reload the page now
      window.location.reload()
    },
    [access_token, languageCode, languageCodeState, setLanguageCodeState],
  )

  return (
    <div data-testid="LanguageSelector">
      <LanguageSelectWrapper>
        <CountryFlagIconWrapper>
          <GlobeIcon />
        </CountryFlagIconWrapper>
        <LanguageSelect
          id={id}
          name="preferences.preferredLanguage"
          onChange={handleOnChange}
          value={languageCodeState ?? ''}
        >
          {Object.values(Languages).map((language) => (
            <option key={language.value} value={language.value}>
              {language.label}
            </option>
          ))}
        </LanguageSelect>
        <SelectorIconWrapper>
          <SelectorIcon />
        </SelectorIconWrapper>
      </LanguageSelectWrapper>
      <PreferredLanguageDialog
        chosenLanguageCode={languageCodeState}
        closeModal={closeModal}
        handleOnConfirm={handleOnConfirm}
        isModalOpen={isModalOpen}
      />
    </div>
  )
}

export default PreferredLanguageSelection

export const Label = styled.label({
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: 1,
})

const CountryFlagIconWrapper = styled.div({
  display: 'flex',
  '> svg': {
    borderRadius: '50%',
    width: 24,
    height: 24,
    padding: 1,
    border: '1px solid black',
    fill: '#1A1A1A',
  },
  left: 0,
  position: 'absolute',
})

const LanguageSelect = styled.select({
  appearance: 'none',
  cursor: 'pointer',
  '&::-ms-expand': {
    display: 'none',
  },
  padding: '0 28px 0 34px',
})

const LanguageSelectWrapper = styled.div({
  alignItems: 'center',
  display: 'flex',
  position: 'relative',
})

const SelectorIconWrapper = styled.div({
  display: 'flex',
  '> svg': {
    transform: 'scale(0.7)',
  },
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
})
